import React, { createContext, useContext, useRef, useState, useCallback, useMemo } from 'react';

import type { Layout, Orientation, PanelSize, SizeValue } from './types';

interface PanelState {
  id: string;
  element: HTMLDivElement | null;
  minSize: number; // Percentage
  maxSize: number; // Percentage
  currentSize: number; // Percentage
  collapsedSize: number; // Percentage
  collapsible: boolean;
  isCollapsed: boolean;
  previousSize: number; // For expand functionality
}

interface SeparatorState {
  id: string;
  element: HTMLDivElement | null;
  panel1Id: string;
  panel2Id: string;
}

interface GroupContextValue {
  groupId: string;
  orientation: Orientation;
  panels: Map<string, PanelState>;
  separators: Map<string, SeparatorState>;
  groupElementRef: React.MutableRefObject<HTMLDivElement | null>;
  registerPanel: (id: string, config: Partial<PanelState>) => void;
  unregisterPanel: (id: string) => void;
  registerSeparator: (id: string, panel1Id: string, panel2Id: string) => void;
  unregisterSeparator: (id: string) => void;
  updatePanelSize: (id: string, size: number) => void;
  getPanelSize: (id: string) => PanelSize | null;
  collapsePanel: (id: string) => void;
  expandPanel: (id: string) => void;
  resizePanel: (id: string, size: SizeValue) => void;
  startDrag: (separatorId: string, clientX: number, clientY: number) => void;
  stopDrag: () => void;
  isDragging: boolean;
  disabled: boolean;
  disableCursor: boolean;
}

const GroupContext = createContext<GroupContextValue | null>(null);

export const useGroupContext = () => {
  const context = useContext(GroupContext);
  if (!context) {
    throw new Error('Panel components must be used within a Group component');
  }
  return context;
};

interface GroupContextProviderProps {
  groupId: string;
  orientation: Orientation;
  disabled?: boolean;
  disableCursor?: boolean;
  onLayoutChange?: (layout: Layout) => void;
  children: React.ReactNode;
}

export const GroupContextProvider: React.FC<GroupContextProviderProps> = ({
  groupId,
  orientation,
  disabled = false,
  disableCursor = false,
  onLayoutChange,
  children,
}) => {
  const [panels] = useState(() => new Map<string, PanelState>());
  const [separators] = useState(() => new Map<string, SeparatorState>());
  const [isDragging, setIsDragging] = useState(false);
  const dragStateRef = useRef<{
    separatorId: string;
    startX: number;
    startY: number;
    panel1StartSize: number;
    panel2StartSize: number;
  } | null>(null);
  const groupElementRef = useRef<HTMLDivElement | null>(null);

  const registerPanel = useCallback(
    (id: string, config: Partial<PanelState>) => {
      const existing = panels.get(id);
      panels.set(id, {
        id,
        element: config.element !== undefined ? config.element : existing?.element ?? null,
        minSize: config.minSize ?? existing?.minSize ?? 0,
        maxSize: config.maxSize ?? existing?.maxSize ?? 100,
        currentSize: config.currentSize ?? existing?.currentSize ?? 0,
        collapsedSize: config.collapsedSize ?? existing?.collapsedSize ?? 0,
        collapsible: config.collapsible ?? existing?.collapsible ?? false,
        isCollapsed: config.isCollapsed ?? existing?.isCollapsed ?? false,
        previousSize: config.previousSize ?? existing?.previousSize ?? 0,
      });
    },
    [panels],
  );

  const unregisterPanel = useCallback(
    (id: string) => {
      panels.delete(id);
    },
    [panels],
  );

  const registerSeparator = useCallback(
    (id: string, panel1Id: string, panel2Id: string) => {
      const existing = separators.get(id);
      separators.set(id, {
        id,
        element: existing?.element ?? null,
        panel1Id,
        panel2Id,
      });
    },
    [separators],
  );

  const unregisterSeparator = useCallback(
    (id: string) => {
      separators.delete(id);
    },
    [separators],
  );

  const getPanelSize = useCallback(
    (id: string): PanelSize | null => {
      const panel = panels.get(id);
      if (!panel || !panel.element || !groupElementRef.current) return null;

      const panelRect = panel.element.getBoundingClientRect();
      const panelSize = orientation === 'horizontal' ? panelRect.width : panelRect.height;

      return {
        asPercentage: panel.currentSize,
        inPixels: panelSize,
      };
    },
    [panels, orientation],
  );

  const updatePanelSize = useCallback(
    (id: string, size: number) => {
      const panel = panels.get(id);
      if (!panel) return;

      const clampedSize = Math.max(panel.minSize, Math.min(panel.maxSize, size));
      panel.currentSize = clampedSize;
      panel.isCollapsed = false;

      // Update flex-basis on the element
      if (panel.element) {
        panel.element.style.flexBasis = `${clampedSize}%`;
      }
    },
    [panels],
  );

  const collapsePanel = useCallback(
    (id: string) => {
      const panel = panels.get(id);
      if (!panel || !panel.collapsible) return;

      panel.previousSize = panel.currentSize;
      panel.currentSize = panel.collapsedSize;
      panel.isCollapsed = true;

      if (panel.element) {
        panel.element.style.flexBasis = `${panel.collapsedSize}%`;
      }
    },
    [panels],
  );

  const expandPanel = useCallback(
    (id: string) => {
      const panel = panels.get(id);
      if (!panel || !panel.collapsible || !panel.isCollapsed) return;

      const sizeToRestore = panel.previousSize > 0 ? panel.previousSize : panel.minSize;
      panel.currentSize = sizeToRestore;
      panel.isCollapsed = false;

      if (panel.element) {
        panel.element.style.flexBasis = `${sizeToRestore}%`;
      }
    },
    [panels],
  );

  const parseSize = useCallback(
    (size: SizeValue, groupSize: number): number => {
      if (typeof size === 'number') {
        // If number is > 100, assume pixels, otherwise percentage
        return size > 100 ? (size / groupSize) * 100 : size;
      }

      const str = String(size).trim();
      if (str.endsWith('%')) {
        return parseFloat(str);
      }
      if (str.endsWith('px')) {
        const pixels = parseFloat(str);
        return (pixels / groupSize) * 100;
      }
      if (str.endsWith('em') || str.endsWith('rem')) {
        // Approximate conversion (1em/rem ≈ 16px typically)
        const emValue = parseFloat(str);
        const pixels = emValue * 16;
        return (pixels / groupSize) * 100;
      }
      if (str.endsWith('vh') || str.endsWith('vw')) {
        const vhValue = parseFloat(str);
        const pixels =
          (vhValue / 100) * (orientation === 'vertical' ? window.innerHeight : window.innerWidth);
        return (pixels / groupSize) * 100;
      }

      // Assume percentage if no unit
      return parseFloat(str) || 0;
    },
    [orientation],
  );

  const resizePanel = useCallback(
    (id: string, size: SizeValue) => {
      const panel = panels.get(id);
      if (!panel || !groupElementRef.current) return;

      const groupRect = groupElementRef.current.getBoundingClientRect();
      const groupSize = orientation === 'horizontal' ? groupRect.width : groupRect.height;
      const percentage = parseSize(size, groupSize);
      updatePanelSize(id, percentage);
    },
    [panels, orientation, parseSize, updatePanelSize],
  );

  const startDrag = useCallback(
    (separatorId: string, clientX: number, clientY: number) => {
      if (disabled) return;

      const separator = separators.get(separatorId);
      if (!separator) return;

      const panel1 = panels.get(separator.panel1Id);
      const panel2 = panels.get(separator.panel2Id);
      if (!panel1 || !panel2 || !panel1.element || !panel2.element || !groupElementRef.current)
        return;

      // Read actual sizes from DOM to handle panels without explicit defaultSize
      const groupRect = groupElementRef.current.getBoundingClientRect();
      const groupSize = orientation === 'horizontal' ? groupRect.width : groupRect.height;

      const panel1Rect = panel1.element.getBoundingClientRect();
      const panel1ActualSize = orientation === 'horizontal' ? panel1Rect.width : panel1Rect.height;
      const panel1Percent =
        groupSize > 0 ? (panel1ActualSize / groupSize) * 100 : panel1.currentSize;

      const panel2Rect = panel2.element.getBoundingClientRect();
      const panel2ActualSize = orientation === 'horizontal' ? panel2Rect.width : panel2Rect.height;
      const panel2Percent =
        groupSize > 0 ? (panel2ActualSize / groupSize) * 100 : panel2.currentSize;

      // Update panel state with actual sizes
      panel1.currentSize = panel1Percent;
      panel2.currentSize = panel2Percent;

      dragStateRef.current = {
        separatorId,
        startX: clientX,
        startY: clientY,
        panel1StartSize: panel1Percent,
        panel2StartSize: panel2Percent,
      };
      setIsDragging(true);
    },
    [disabled, separators, panels, orientation, groupElementRef],
  );

  const stopDrag = useCallback(() => {
    dragStateRef.current = null;
    setIsDragging(false);
  }, []);

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!dragStateRef.current || !groupElementRef.current) {
        return;
      }

      e.preventDefault();

      const { separatorId, startX, startY, panel1StartSize, panel2StartSize } =
        dragStateRef.current;
      const separator = separators.get(separatorId);
      if (!separator) {
        return;
      }

      const panel1 = panels.get(separator.panel1Id);
      const panel2 = panels.get(separator.panel2Id);
      if (!panel1 || !panel2 || !panel1.element || !panel2.element) {
        return;
      }

      const groupRect = groupElementRef.current.getBoundingClientRect();
      const groupSize = orientation === 'horizontal' ? groupRect.width : groupRect.height;
      if (groupSize === 0) {
        return;
      }

      const delta = orientation === 'horizontal' ? e.clientX - startX : e.clientY - startY;
      const deltaPercent = (delta / groupSize) * 100;

      const newPanel1Size =
        orientation === 'horizontal'
          ? panel1StartSize + deltaPercent
          : panel1StartSize + deltaPercent; // Dragging down grows top panel
      const newPanel2Size =
        orientation === 'horizontal'
          ? panel2StartSize - deltaPercent
          : panel2StartSize - deltaPercent; // Dragging down shrinks bottom panel

      // Apply constraints
      const clampedPanel1Size = Math.max(panel1.minSize, Math.min(panel1.maxSize, newPanel1Size));
      const clampedPanel2Size = Math.max(panel2.minSize, Math.min(panel2.maxSize, newPanel2Size));

      // Adjust if constraints were hit
      const totalRequested = newPanel1Size + newPanel2Size;
      const totalClamped = clampedPanel1Size + clampedPanel2Size;
      const adjustment = totalRequested - totalClamped;

      if (adjustment !== 0) {
        // Distribute adjustment
        const panel1Adjustment =
          (clampedPanel1Size / (clampedPanel1Size + clampedPanel2Size)) * adjustment;
        const panel2Adjustment = adjustment - panel1Adjustment;

        panel1.currentSize = Math.max(
          panel1.minSize,
          Math.min(panel1.maxSize, clampedPanel1Size + panel1Adjustment),
        );
        panel2.currentSize = Math.max(
          panel2.minSize,
          Math.min(panel2.maxSize, clampedPanel2Size + panel2Adjustment),
        );
      } else {
        panel1.currentSize = clampedPanel1Size;
        panel2.currentSize = clampedPanel2Size;
      }

      // Update DOM
      panel1.element.style.flexBasis = `${panel1.currentSize}%`;
      panel2.element.style.flexBasis = `${panel2.currentSize}%`;
    },
    [orientation, separators, panels, groupElementRef],
  );

  const handleMouseUp = useCallback(() => {
    if (dragStateRef.current && onLayoutChange) {
      const layout: Layout = {};
      panels.forEach((panel, id) => {
        layout[id] = panel.currentSize;
      });
      onLayoutChange(layout);
    }
    stopDrag();
  }, [onLayoutChange, panels, stopDrag]);

  React.useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);

      document.body.style.userSelect = 'none';
      document.body.style.cursor = orientation === 'horizontal' ? 'col-resize' : 'row-resize';
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
        document.body.style.userSelect = '';
        document.body.style.cursor = '';
      };
    }
  }, [isDragging, handleMouseMove, handleMouseUp, orientation]);

  const value = useMemo<GroupContextValue>(
    () => ({
      groupId,
      orientation,
      panels,
      separators,
      groupElementRef,
      registerPanel,
      unregisterPanel,
      registerSeparator,
      unregisterSeparator,
      updatePanelSize,
      getPanelSize,
      collapsePanel,
      expandPanel,
      resizePanel,
      startDrag,
      stopDrag,
      isDragging,
      disabled,
      disableCursor,
    }),
    [
      groupId,
      orientation,
      panels,
      separators,
      registerPanel,
      unregisterPanel,
      registerSeparator,
      unregisterSeparator,
      updatePanelSize,
      getPanelSize,
      collapsePanel,
      expandPanel,
      resizePanel,
      startDrag,
      stopDrag,
      isDragging,
      disabled,
      disableCursor,
    ],
  );

  return <GroupContext.Provider value={value}>{children}</GroupContext.Provider>;
};
